import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { unwrapV2, V2ApiResponse, v2ErrorMessage } from '../../shared/utils/v2-api-response.util';
import {
  EMPTY_PROJECT_SUMMARY,
  ProjectDetail,
  ProjectDocumentType,
  ProjectExportFile,
  ProjectListFilter,
  ProjectAlert,
  ProjectListResult,
  ProjectManagerHistoryRow,
  ProjectCategoryMovements,
  ProjectNonprofitExpenseReport,
  ProjectRecord,
  ProjectReport,
} from '../models/project.model';

@Injectable({ providedIn: 'root' })
export class ProjectsService {
  private readonly baseUrl = environment.apiUrl + '/api/v2/projects';

  constructor(private http: HttpClient) { }

  list(filter: ProjectListFilter): Promise<ProjectListResult> {
    const fallback = 'No se pudo cargar la lista de proyectos';
    return firstValueFrom(this.http.post<any>(`${this.baseUrl}/list`, filter))
      .catch(error => {
        throw { status: error && error.status, error: { message: v2ErrorMessage(error && error.error, fallback) } };
      })
      .then(response => {
        if (!response || response.isSuccess === false) {
          throw { error: { message: v2ErrorMessage(response, fallback) } };
        }
        return {
          items: Array.isArray(response.values) ? response.values : [],
          total: Number(response.total) || 0,
          summary: response.summary || { ...EMPTY_PROJECT_SUMMARY },
        };
      });
  }

  get(uid: string): Promise<ProjectRecord> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<ProjectRecord>>(`${this.baseUrl}/${uid}`)),
      'No se pudo cargar el proyecto'
    );
  }

  create(payload: ProjectRecord): Promise<string> {
    return unwrapV2(
      firstValueFrom(this.http.post<V2ApiResponse<string>>(this.baseUrl, payload)),
      'Ha ocurrido un error al crear el proyecto'
    );
  }

  update(uid: string, payload: ProjectRecord): Promise<boolean> {
    return unwrapV2(
      firstValueFrom(this.http.put<V2ApiResponse<boolean>>(`${this.baseUrl}/${uid}`, { ...payload, uid })),
      'Ha ocurrido un error al actualizar el proyecto'
    );
  }

  // Activar/desactivar: los inactivos se ocultan del listado de Asociaciones.
  setActive(uid: string, isActive: boolean): Promise<boolean> {
    return unwrapV2(
      firstValueFrom(this.http.put<V2ApiResponse<boolean>>(`${this.baseUrl}/${uid}/active`, { isActive })),
      'No se pudo cambiar el estado del proyecto'
    );
  }

  delete(uid: string): Promise<boolean> {
    return unwrapV2(
      firstValueFrom(this.http.delete<V2ApiResponse<boolean>>(`${this.baseUrl}/${uid}`)),
      'Ha ocurrido un error al eliminar el proyecto'
    );
  }

  detail(uid: string): Promise<ProjectDetail> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<ProjectDetail>>(`${this.baseUrl}/${uid}/detail`)),
      'No se pudo cargar el detalle del proyecto'
    ).then(detail => ({
      uid: detail && detail.uid ? detail.uid : uid,
      investors: detail && Array.isArray(detail.investors) ? detail.investors : [],
      investedCapital: detail ? Number(detail.investedCapital) || 0 : 0,
      documents: detail && Array.isArray(detail.documents) ? detail.documents : [],
    }));
  }

  report(uid: string): Promise<ProjectReport> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<ProjectReport>>(`${this.baseUrl}/${uid}/report`)),
      'No se pudo cargar el informe del proyecto'
    ).then(report => ({
      ...report,
      expensesByType: report && Array.isArray(report.expensesByType) ? report.expensesByType : [],
      investors: report && Array.isArray(report.investors) ? report.investors : [],
    }));
  }

  // Últimos movimientos de una categoría del informe; con grantUid, solo los de esa financiación.
  categoryMovements(uid: string, category: string, grantUid?: string | null): Promise<ProjectCategoryMovements> {
    const params: Record<string, string> = { category };
    if (grantUid) params['grantUid'] = grantUid;
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<ProjectCategoryMovements>>(`${this.baseUrl}/${uid}/nonprofit-category-movements`, { params })),
      'No se pudieron cargar los movimientos de la categoría'
    ).then(dto => ({ ...dto, items: dto && Array.isArray(dto.items) ? dto.items : [] }));
  }

  nonprofitExpenseReport(uid: string): Promise<ProjectNonprofitExpenseReport> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<ProjectNonprofitExpenseReport>>(`${this.baseUrl}/${uid}/nonprofit-expense-report`)),
      'No se pudo cargar el desglose de gastos justificados'
    ).then(report => ({
      ...report,
      categories: report && Array.isArray(report.categories) ? report.categories : [],
    }));
  }

  managersHistory(uid: string): Promise<ProjectManagerHistoryRow[]> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<ProjectManagerHistoryRow[]>>(`${this.baseUrl}/${uid}/managers-history`)),
      'No se pudo cargar el histórico de responsables'
    ).then(list => Array.isArray(list) ? list : []);
  }

  alerts(uid: string): Promise<ProjectAlert[]> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<ProjectAlert[]>>(`${this.baseUrl}/${uid}/alerts`)),
      'No se pudo cargar el informe de alertas'
    ).then(list => (Array.isArray(list) ? list : []).map(alert => ({
      ...alert,
      documents: Array.isArray(alert.documents) ? alert.documents : [],
    })));
  }

  exportExcel(uid: string): Promise<ProjectExportFile> {
    return firstValueFrom(this.http.get(`${this.baseUrl}/${uid}/export-excel`, { responseType: 'blob', observe: 'response' }))
      .then((response: HttpResponse<Blob>) => {
        if (!response.body || !response.body.size) throw new Error();
        return { blob: response.body, fileName: this.fileName(response) };
      });
  }

  assign(documentType: ProjectDocumentType, documentUid: string, projectUid: string | null): Promise<boolean> {
    return unwrapV2(
      firstValueFrom(this.http.put<V2ApiResponse<boolean>>(`${this.baseUrl}/assign`, { documentType, documentUid, projectUid })),
      'No se pudo guardar el proyecto del documento'
    );
  }

  private fileName(response: HttpResponse<Blob>): string {
    const header = response.headers.get('content-disposition') || '';
    const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(header);
    const raw = match ? (match[1] || match[2]) : '';
    if (!raw) return 'Proyecto.xlsx';
    try {
      return decodeURIComponent(raw.trim());
    } catch {
      return raw.trim();
    }
  }
}
